import type { CellUploadInput } from '@components/CustomAllocationEditor/cellStates';
import { getAccountNumber } from '@interfaces/account';
import type { PortfolioSecurity } from '@ovComponents/4-module/widgets/portfolio/components/interfaces';
import {
  displayPercentToFraction,
  fractionToDisplayPercent,
} from '@utils/allocationUnits';
import Papa from 'papaparse';
import {
  type BuildCsvTemplateOptions,
  csvTemplateLayout,
} from './buildCsvTemplate';
import { cellFraction } from './placeholderMatrix';
import type {
  AccountAllocationGridAccount,
  AccountAllocationGridReport,
} from './types';

export const MAX_ACCOUNT_ALLOCATION_CSV_FILE_MB = 5;
export const MAX_ACCOUNT_ALLOCATION_CSV_FUND_ROWS = 200;

export const AccountAllocationCsvIssueCodes = {
  WRONG_FILE_TYPE: 'WRONG_FILE_TYPE',
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  UNREADABLE_FILE: 'UNREADABLE_FILE',
  TOO_MANY_ROWS: 'TOO_MANY_ROWS',
  NO_ACCOUNT_COLUMNS: 'NO_ACCOUNT_COLUMNS',
  UNKNOWN_ACCOUNT: 'UNKNOWN_ACCOUNT',
  READ_ONLY_ACCOUNT: 'READ_ONLY_ACCOUNT',
  DUPLICATE_ACCOUNT: 'DUPLICATE_ACCOUNT',
  DUPLICATE_FUND: 'DUPLICATE_FUND',
  INVALID_VALUE: 'INVALID_VALUE',
  VALUE_OUT_OF_RANGE: 'VALUE_OUT_OF_RANGE',
  FUND_NOT_HELD: 'FUND_NOT_HELD',
  UNKNOWN_FUND: 'UNKNOWN_FUND',
  FUND_MATCHED_BY_TICKER: 'FUND_MATCHED_BY_TICKER',
  MISSING_ACCOUNT: 'MISSING_ACCOUNT',
} as const;
export type AccountAllocationCsvIssueCode =
  (typeof AccountAllocationCsvIssueCodes)[keyof typeof AccountAllocationCsvIssueCodes];

// Everything else is an error and blocks the whole upload.
const WARNING_CODES: ReadonlySet<AccountAllocationCsvIssueCode> = new Set([
  AccountAllocationCsvIssueCodes.UNKNOWN_FUND,
  AccountAllocationCsvIssueCodes.FUND_MATCHED_BY_TICKER,
  AccountAllocationCsvIssueCodes.MISSING_ACCOUNT,
]);

type AccountAllocationCsvIssueDetails = {
  // Spreadsheet coordinates, 1-based, with the header as row 1.
  row?: number;
  column?: number;
  // The fund as the file names it: ticker, or name when it has no ticker.
  fund?: string;
  financialProductId?: string;
  accountNumber?: string;
  subAccountId?: string;
  // The text in the file that caused the issue.
  value?: string;
};

export type AccountAllocationCsvIssue = AccountAllocationCsvIssueDetails & {
  severity: 'error' | 'warning';
  code: AccountAllocationCsvIssueCode;
};

export type ParsedAccountAllocationCsv = {
  cells: CellUploadInput[];
  issues: AccountAllocationCsvIssue[];
};

type CurrentFraction = (
  subAccountId: string,
  financialProductId: string,
  modelTarget: number,
) => number;

type AccountColumn = {
  index: number;
  account: AccountAllocationGridAccount;
  accountNumber: string;
};

const HEADER_ROWS = 1;
const SECURITY_COLUMN = 0;
const FUND_ID_COLUMN = 1;
const FIRST_ACCOUNT_COLUMN = 2;

// Unsigned or negative plain decimals only: no exponent, percent sign or
// thousands separator. A negative still parses so it reports as out of range.
const PLAIN_DECIMAL = /^-?(\d+(\.\d*)?|\.\d+)$/;
const MAX_PERCENT = 100;

export const hasBlockingIssue = (issues: AccountAllocationCsvIssue[]) =>
  issues.some((issue) => issue.severity === 'error');

export const issueOf = (
  code: AccountAllocationCsvIssueCode,
  details: AccountAllocationCsvIssueDetails = {},
): AccountAllocationCsvIssue => ({
  severity: WARNING_CODES.has(code) ? 'warning' : 'error',
  code,
  ...details,
});

// The template prefixes text starting with = + - @ with ' so a spreadsheet
// does not run it as a formula.
const textCell = (value: string | undefined) =>
  (value ?? '').trim().replace(/^'/, '');

const headerAccountNumber = (header: string | undefined) =>
  textCell(header)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .at(-1) ?? '';

const accountNumberOf = (account: AccountAllocationGridAccount) =>
  getAccountNumber({
    id: account.subAccount.account.id,
    custodianAccountNumber: account.subAccount.account.custodianAccountNumber,
  });

const readGrid = (text: string) => {
  const { data, errors } = Papa.parse<string[]>(text, {
    header: false,
    delimiter: ',',
    skipEmptyLines: 'greedy',
  });
  return errors.length > 0 || data.length === 0 ? null : data;
};

const groupByAccountNumber = (accounts: AccountAllocationGridAccount[]) => {
  const groups = new Map<string, AccountAllocationGridAccount[]>();
  for (const account of accounts) {
    const accountNumber = accountNumberOf(account);
    groups.set(accountNumber, [...(groups.get(accountNumber) ?? []), account]);
  }
  return groups;
};

// Excel writes a blank column for any cell ever used past the last one.
const isEmptyColumn = (rows: string[][], index: number) =>
  rows.every((row) => !row[index]?.trim());

const columnIssueCode = (
  accountNumber: string,
  tradeableNumbers: Set<string>,
  readOnlyNumbers: Set<string>,
) => {
  if (tradeableNumbers.has(accountNumber)) {
    return AccountAllocationCsvIssueCodes.DUPLICATE_ACCOUNT;
  }
  if (readOnlyNumbers.has(accountNumber)) {
    return AccountAllocationCsvIssueCodes.READ_ONLY_ACCOUNT;
  }
  return AccountAllocationCsvIssueCodes.UNKNOWN_ACCOUNT;
};

// Columns are matched by account number, not position. Sub-accounts of one
// account share a number, so their columns pair up in the template's order.
const mapAccountColumns = (
  [header, ...rows]: string[][],
  report: AccountAllocationGridReport,
  tradeableAccounts: AccountAllocationGridAccount[],
) => {
  const unclaimed = groupByAccountNumber(tradeableAccounts);
  const tradeableNumbers = new Set(unclaimed.keys());
  const tradeableIds = new Set(
    tradeableAccounts.map((account) => account.subAccount.id),
  );
  const readOnlyNumbers = new Set(
    report.subAccounts
      .filter((account) => !tradeableIds.has(account.subAccount.id))
      .map(accountNumberOf),
  );

  const columns: AccountColumn[] = [];
  const issues: AccountAllocationCsvIssue[] = [];
  for (let index = FIRST_ACCOUNT_COLUMN; index < header.length; index += 1) {
    const accountNumber = headerAccountNumber(header[index]);
    if (!accountNumber && isEmptyColumn(rows, index)) continue;
    const account = unclaimed.get(accountNumber)?.shift();
    if (account) {
      columns.push({ index, account, accountNumber });
      continue;
    }
    issues.push(
      issueOf(
        columnIssueCode(accountNumber, tradeableNumbers, readOnlyNumbers),
        { column: index + 1, accountNumber },
      ),
    );
  }

  for (const [accountNumber, accounts] of unclaimed) {
    for (const account of accounts) {
      issues.push(
        issueOf(AccountAllocationCsvIssueCodes.MISSING_ACCOUNT, {
          accountNumber,
          subAccountId: account.subAccount.id,
        }),
      );
    }
  }

  return { columns, issues };
};

const fundLabel = (security: PortfolioSecurity) =>
  security.ticker || security.translatedName;

// Fund ID first. A ticker (or the name, for a fund without one) only counts
// when exactly one fund has it.
const fundMatcher = (securities: PortfolioSecurity[]) => {
  const byId = new Map(securities.map((security) => [security.id, security]));
  const byLabel = new Map<string, PortfolioSecurity[]>();
  for (const security of securities) {
    const label = fundLabel(security);
    byLabel.set(label, [...(byLabel.get(label) ?? []), security]);
  }

  return (fund: string, financialProductId: string, row: number) => {
    const byFundId = byId.get(financialProductId);
    if (byFundId) return { security: byFundId };

    const [byTicker, ...others] = byLabel.get(fund) ?? [];
    if (!byTicker || others.length > 0) {
      return {
        issue: issueOf(AccountAllocationCsvIssueCodes.UNKNOWN_FUND, {
          row,
          fund,
          financialProductId,
        }),
      };
    }
    return {
      security: byTicker,
      issue: issueOf(AccountAllocationCsvIssueCodes.FUND_MATCHED_BY_TICKER, {
        row,
        fund,
        financialProductId: byTicker.id,
        value: financialProductId,
      }),
    };
  };
};

type CellContext = {
  security: PortfolioSecurity;
  column: AccountColumn;
  isAssetClassPortfolio: boolean;
  currentFraction: CurrentFraction;
  details: AccountAllocationCsvIssueDetails;
};

const readCell = (
  value: string,
  context: CellContext,
): { cell?: CellUploadInput; issue?: AccountAllocationCsvIssue } => {
  if (value === '') return {};

  const { security, column, details } = context;
  if (!PLAIN_DECIMAL.test(value)) {
    return {
      issue: issueOf(AccountAllocationCsvIssueCodes.INVALID_VALUE, details),
    };
  }
  const percent = Number(value);
  if (percent < 0 || percent > MAX_PERCENT) {
    return {
      issue: issueOf(
        AccountAllocationCsvIssueCodes.VALUE_OUT_OF_RANGE,
        details,
      ),
    };
  }

  const held =
    cellFraction(
      column.account,
      {
        expectedPercentage: security.expectedPercentage,
        financialProduct: { id: security.id },
      },
      context.isAssetClassPortfolio,
    ) !== undefined;
  if (!held) {
    return percent === 0
      ? {}
      : {
          issue: issueOf(AccountAllocationCsvIssueCodes.FUND_NOT_HELD, details),
        };
  }

  // Compared at the grid's two decimals, so display rounding in the file
  // never reads as an edit.
  const subAccountId = column.account.subAccount.id;
  const fraction = displayPercentToFraction(value);
  const shown = context.currentFraction(
    subAccountId,
    security.id,
    security.expectedPercentage,
  );
  if (fractionToDisplayPercent(shown) === fractionToDisplayPercent(fraction)) {
    return {};
  }
  return {
    cell: { subAccountId, financialProductId: security.id, fraction },
  };
};

/**
 * Reads an account allocation CSV, in the format WF-1907's template writes,
 * back onto the grid. Only values that differ from what the grid shows become
 * cells, so an unchanged template uploads nothing. Callers must not apply the
 * cells when any issue is an error.
 */
export const parseAccountAllocationCsv = (
  text: string,
  report: AccountAllocationGridReport,
  currentFraction: CurrentFraction,
  options: BuildCsvTemplateOptions,
): ParsedAccountAllocationCsv => {
  const grid = readGrid(text);
  if (!grid) {
    return {
      cells: [],
      issues: [issueOf(AccountAllocationCsvIssueCodes.UNREADABLE_FILE)],
    };
  }

  const [header, ...rows] = grid;
  if (rows.length > MAX_ACCOUNT_ALLOCATION_CSV_FUND_ROWS) {
    return {
      cells: [],
      issues: [issueOf(AccountAllocationCsvIssueCodes.TOO_MANY_ROWS)],
    };
  }
  if (header.length <= FIRST_ACCOUNT_COLUMN) {
    return {
      cells: [],
      issues: [issueOf(AccountAllocationCsvIssueCodes.NO_ACCOUNT_COLUMNS)],
    };
  }

  const { isAssetClassPortfolio, accounts, securities } = csvTemplateLayout(
    report,
    options,
  );
  const { columns, issues } = mapAccountColumns(grid, report, accounts);
  const matchFund = fundMatcher(securities);
  const seenFunds = new Set<string>();
  const cells: CellUploadInput[] = [];

  rows.forEach((cellsInRow, rowIndex) => {
    const row = rowIndex + HEADER_ROWS + 1;
    const fund = textCell(cellsInRow[SECURITY_COLUMN]);
    const match = matchFund(fund, textCell(cellsInRow[FUND_ID_COLUMN]), row);
    if (match.issue) issues.push(match.issue);
    const { security } = match;
    if (!security) return;

    if (seenFunds.has(security.id)) {
      issues.push(
        issueOf(AccountAllocationCsvIssueCodes.DUPLICATE_FUND, {
          row,
          fund,
          financialProductId: security.id,
        }),
      );
      return;
    }
    seenFunds.add(security.id);

    for (const column of columns) {
      const value = (cellsInRow[column.index] ?? '').trim();
      const result = readCell(value, {
        security,
        column,
        isAssetClassPortfolio,
        currentFraction,
        details: {
          row,
          column: column.index + 1,
          fund,
          financialProductId: security.id,
          accountNumber: column.accountNumber,
          subAccountId: column.account.subAccount.id,
          value,
        },
      });
      if (result.cell) cells.push(result.cell);
      if (result.issue) issues.push(result.issue);
    }
  });

  return { cells, issues };
};
